import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import queryString from 'query-string';

import useQueryMatch from 'hooks/useQueryMatch';
import { SecurityPageTarget } from './enums/SecurityPageTarget.enum';

import BOHeader from 'components/Backoffice/Landing/HomeHeader';
import MPHeader from 'components/Marketplace/Landing/HomeHeader';
import CorporateHeader from 'components/Corporate/Header';
import MainHeader from 'components/MainLanding/Header';

import Footer from '../Backoffice/EorEmployerOfRecordServices/Footer';

import style from './SecurityPage.module.scss';

const DEFAULT_SECTION = SecurityPageTarget.Main;
const PAGE_SECTIONS = Object.values(SecurityPageTarget);

export default function SecurityPage() {
  const history = useHistory();
  const query = queryString.parse(history.location.search);
  const pageSectionMatch = useQueryMatch(PAGE_SECTIONS);

  useEffect(() => {
    if (!pageSectionMatch?.isExact) {
      history.replace(`${history.location.pathname}?target=${DEFAULT_SECTION}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageSectionMatch?.isExact]);

  const getHeader = () => {
    switch (query.target) {
      case SecurityPageTarget.BO:
        return <BOHeader />;

      case SecurityPageTarget.MP:
        return <MPHeader />;

      case SecurityPageTarget.Corporate:
        return <CorporateHeader />;

      case SecurityPageTarget.Main:
      default:
        return <MainHeader />;
    }
  };

  return (
    <>
      {getHeader()}
      <main className="typography">
        <section className={style.heroSection}>
          <div className="container">
            <h1 className="info-title underline">
              <span>
                myBas<span className="fake"></span>Pay
              </span>{' '}
              security
            </h1>
            <h3 className="info-subtitle">All data is securely protected.</h3>
            <div className={style.heroBg}>
              <div className={style.heroText}>
                <p>
                  As a back-office provider and technology platform for
                  Contingent Workforce and Corporate Talent Acquisition, we
                  handle and process high volumes of data. Much of this data
                  includes personal information and financial transactions,
                  which we treat with the highest sense of urgency.
                </p>
                <p>
                  Our team of information security architects and engineers have
                  implemented the strictest protocols backed by a robust
                  infrastructure and AI-powered monitoring to ensure we are
                  ahead of the curve for data protection.
                </p>
              </div>
            </div>
          </div>
        </section>
        <section className={style.secondSection}>
          <div className="container">
            <h3 className="info-subtitle underline">
              How we are building the most secure platform:
            </h3>
            <ul className="default-list check">
              <li>
                Documented security playbooks for all infrastructure and
                application stacks;
              </li>
              <li>
                Top-notch Infrastructure as Code (IaC) with built-in security
                for all AWS services;
              </li>
              <li>
                Consistent, always-on Security as Code for our infrastructure;
              </li>
              <li>
                Based on security defaults recommendations and best practices
                for AWS security;
              </li>
              <li>
                AWS: Strict IAM configuration, auditing and logging for
                configuration changes, 2FA - Strong authentication for IAM
                users;
              </li>
              <li>
                Container security with image scanning and sane defaults options
                for container orchestration layers;
              </li>
              <li>
                Centralized security frameworks provide a certified and
                consistent layer of security across the application stack;
              </li>
              <li>Web Application Firewalls prevent attacks;</li>
              <li>
                Security tuned load balancer protects against Denial of Service
                (DoS) attacks;
              </li>
              <li>
                Strict layer 3 firewall rules in AWS and VPC configurations;
              </li>
              <li>
                Annual Pentest and Cloud Security validation simulation
                exercises.
              </li>
            </ul>
          </div>
        </section>
        <section className={style.thirdSection}>
          <div className="container">
            <h3 className="info-subtitle underline">
              We ensure our entire organization is well trained and up to date
              on all security protocols with:
            </h3>
            <ul className="default-list rectangle">
              <li>Continuous developer security training;</li>
              <li>
                Peer code review (with focus on security) for all merges to the
                master branch;
              </li>
              <li>
                Security automations and AI-powered monitoring tools that scan
                code and applications for security vulnerabilities and
                anomalies;
              </li>
              <li>PII encryption of data in the database layer.</li>
            </ul>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
